import { BasicResponse, PagedResponse } from '../types';
import { DasWBGTAlert, RequestAlertsParams } from '../types/Alert';
import {
  DasWBGT,
  HeatstrokeLatest,
  HeatstrokeStats,
  PSILabelTimeLine,
  WBGTheatstrokeStats,
} from '../types/Device';

import { delay } from '../utils/common';

import DsmAxios from './DsmAxios';

export const getUserDasWbgts = async (params?: {
  dasId?: string;
  id?: string;
  projectId?: string;
  assignedProject?: boolean;
  nextCursor?: string;
  limit?: number;
}) => {
  /**
   * limit too large will cause server response timeout
   */
  let limit = params?.limit ?? 100;
  if (import.meta.env.MODE === 'development') {
    limit = params?.limit ?? 100;
  }
  await delay(100);
  return DsmAxios.get<PagedResponse<DasWBGT>>(`v1/daswbgts`, {
    params: {
      ...params,
      limit,
    },
  });
};

interface GetProjectDasWBGTArgs {
  projectId: string;
  params?: {
    orderBy?: 'das_id' | 'last_data_point_received_at';
    limit?: number;
    resourceId?: string;
    prevCursor?: string;
    nextCursor?: string;
    id?: string;
    dasId?: string;
    managedOrg?: string;
  };
}

export const getProjectDasWBGT = (data: GetProjectDasWBGTArgs) => {
  return DsmAxios.get<PagedResponse<DasWBGT>>(
    `v1/projects/${data.projectId}/daswbgts`,
    {
      params: data.params,
    },
  );
};

export const getDasWBGTAlerts = (
  projectId: string,
  params?: RequestAlertsParams & {
    resourceId?: string;
  },
) => {
  return DsmAxios.get<PagedResponse<DasWBGTAlert>>(
    `v1/projects/${projectId}/daswbgts-alerts`,
    { params },
  );
};

export const dismissDasWBGTAlert = (projectId: string, alertId: string) => {
  return DsmAxios.post(
    `v1/projects/${projectId}/daswbgts-alerts/${alertId}/dismiss`,
  );
};

export const undismissDasWBGTAlert = (projectId: string, alertId: string) => {
  return DsmAxios.post(
    `v1/projects/${projectId}/daswbgts-alerts/${alertId}/undismiss`,
  );
};

export const readDasWBGTAlert = (projectId: string, alertId: string) => {
  return DsmAxios.post(
    `v1/projects/${projectId}/daswbgts-alerts/${alertId}/read`,
  );
};

export interface RequestDasWBGTTaskArgs {
  projectId: string;
  payload?: {
    endpointId: string;
    type:
      | 'smartlockStaticPasswordUnlock'
      | 'smartlockSublockUnlock'
      | 'smartlockAuthorizedCardManagement'
      | 'smartlockRegisterCardManagement';
    enableRegistration?: boolean;
    cards?: string[];
  };
}

export const requestDasWBGTTask = (args: RequestDasWBGTTaskArgs) => {
  return DsmAxios.post(
    `/v1/projects/${args.projectId}/daswbgt-tasks`,
    args.payload,
  );
};

export const getWorkerPSI_RPE = (
  projectId: string,
  target: 'psi' | 'rpe',
  params: {
    fromDate: string;
    toDate: string;
    limit?: number;
    order: 'desc' | 'asc';
    nextCursor?: string;
    workerId?: string;
    interval?: 'month' | 'hour' | 'day' | '15m';
  },
) => {
  return DsmAxios.get<BasicResponse<PSILabelTimeLine[]>>(
    `v1/projects/${projectId}/stats/heatstroke/${target}`,
    {
      params,
    },
  );
};

export const getWorkerHeatStrokeStats = (
  projectId: string,
  params: {
    fromDate: string;
    toDate: string;
    limit?: number;
    order: 'desc' | 'asc';
    nextCursor?: string;
    workerId?: string;
    interval?: 'month' | 'hour' | 'day' | '15m';
    timeZone?: string;
  },
) => {
  return DsmAxios.get<BasicResponse<HeatstrokeStats>>(
    `v1/projects/${projectId}/stats/heatstroke`,
    {
      params,
    },
  );
};

export const getWorkerHeatStrokeWBGTStats = (
  projectId: string,
  params: {
    fromDate: string;
    toDate: string;
    limit?: number;
    order: 'desc' | 'asc';
    nextCursor?: string;
    interval?: 'month' | 'hour' | 'day' | '15m';
    timeZone?: string;
    dasId: string;
  },
) => {
  return DsmAxios.get<BasicResponse<WBGTheatstrokeStats>>(
    `v1/projects/${projectId}/stats/heatstroke/wbgt-stats`,
    {
      params,
    },
  );
};

export const getHeatstrokeGroupByWorker = (
  projectId: string,
  params: {
    groupBy?: 'workerId';
  },
) => {
  return DsmAxios.get<BasicResponse<HeatstrokeLatest[]>>(
    `v1/projects/${projectId}/stats/heatstroke/latest`,
    {
      params,
    },
  );
};
